"use client";

import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  getBrandBySlug,
  getBrandProductsPage,
  type BrandProduct,
  type BrandSummary,
} from "@/features/brand/services/brand.service";

const BRAND_PAGE_SIZE = 20;

type BrandProductsResponse = Awaited<ReturnType<typeof getBrandProductsPage>>;

export function useBrandSlugPage(slug: string) {
  const [page, setPage] = useState(1);
  const [products, setProducts] = useState<BrandProduct[]>([]);
  const [totalProducts, setTotalProducts] = useState(0);

  useEffect(() => {
    setPage(1);
    setProducts([]);
    setTotalProducts(0);
  }, [slug]);

  const brandQuery = useQuery({
    queryKey: ["brand", "summary", slug],
    queryFn: () => getBrandBySlug(slug),
  });

  const productsQuery = useQuery({
    queryKey: ["brand", "products", slug, page],
    queryFn: () => getBrandProductsPage(slug, page, BRAND_PAGE_SIZE),
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    const nextPage = productsQuery.data as BrandProductsResponse | undefined;
    if (!nextPage) {
      return;
    }

    const nextProducts = Array.isArray(nextPage.data) ? nextPage.data : [];
    setProducts((currentProducts) => {
      if (page === 1) {
        return nextProducts;
      }

      const seen = new Set(currentProducts.map((product) => product.id));
      const appendedProducts = nextProducts.filter((product) => !seen.has(product.id));
      return [...currentProducts, ...appendedProducts];
    });
    setTotalProducts(nextPage.meta?.total ?? 0);
  }, [page, productsQuery.data]);

  const totalPages = useMemo(
    () => Math.ceil((totalProducts || 0) / BRAND_PAGE_SIZE),
    [totalProducts],
  );

  return {
    brand: brandQuery.data as BrandSummary | null | undefined,
    products,
    totalProducts,
    totalPages,
    page,
    setPage,
    loading: brandQuery.isPending || (page === 1 && productsQuery.isPending),
    loadingMore: page > 1 && productsQuery.isFetching,
    hasMore: page < totalPages,
    limit: BRAND_PAGE_SIZE,
  } as const;
}
